import { Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import CardHeader from 'react-bootstrap/esm/CardHeader';
import WarningRedIcon from '../../../assets/warning-red-icon.svg';
import { PrometeoData } from '../../../types/add_commerce.types';
import { BankAccountRequest } from '../../../types/business/request';
import { CurrencyEnum } from '../../../types/enums/currency.enum';
import { AccountType, Bank, bankAccountTypes, banks } from '../../../utils/data';
import { capitalize } from '../../../utils/helpers';

interface ConfirmBankProps {
  show: boolean;
  toggleModal: () => void;
  values: BankAccountRequest;
  prometeoValues: PrometeoData[];
  handleNextStep: (values: BankAccountRequest) => void;
  prometeoError: boolean;
}

const accountTitle = (index: number, suffix = '') => `Cuenta Bancaria${index ? ` ${index + 1}` : ''}${suffix}`;

const AccountFields = ({ val }: { val: any }) => (
  <>
    <Row>
      <Col>
        <p>Banco</p>
      </Col>
      <Col>
        <p>{banks.find((bank: Bank) => bank.id === val?.bankExternalId)?.name}</p>
      </Col>
    </Row>
    <hr />
    <Row>
      <Col>
        <p>Cuenta N°</p>
        <p>Tipo de cuenta</p>
        <p>Moneda</p>
        <p>Sucursal</p>
      </Col>
      <Col>
        <p>{val?.accountNumber}</p>
        <p>
          {capitalize(
            bankAccountTypes.find(
              (bankAccountType: AccountType) => bankAccountType.value === Number(val?.accountType),
            )?.name,
          )}
        </p>
        <p>{+val?.accountCurrency === CurrencyEnum.URUGUAYAN_PESOS ? 'Pesos uruguayos' : 'Dólares'}</p>
        <p>{val?.accountBranch || '-'}</p>
      </Col>
    </Row>
    <hr />
    <Row>
      <Col>
        <p>Titular</p>
      </Col>
      <Col>
        <p>{val?.accountHolder}</p>
      </Col>
    </Row>
  </>
);

const ValidatedFields = ({ prometeoInfo }: { prometeoInfo?: PrometeoData }) => (
  <Row>
    <Col>
      <p>Titular Real</p>
      <p>Moneda</p>
    </Col>
    <Col>
      <p>{prometeoInfo?.accountHolder}</p>
      <p>{prometeoInfo?.accountCurrency}</p>
    </Col>
  </Row>
);

const ConfirmBankModal = ({
  show,
  toggleModal,
  handleNextStep,
  values,
  prometeoValues,
  prometeoError,
}: ConfirmBankProps) => {
  const findPrometeoInfo = (val: any) =>
    prometeoValues?.find((b: PrometeoData) => b.accountNumber === val?.accountNumber);

  const isAccountPending = (val: any) => !!findPrometeoInfo(val)?.santanderValidationPending;

  const account = (val: any, index: number) => {
    const prometeoInfo = findPrometeoInfo(val);

    return (
      <Container key={index}>
        <Container className="card-red">
          <Row>
            <p className="strong">{accountTitle(index)}</p>
          </Row>
          <AccountFields val={val} />
        </Container>
        {!prometeoError && !isAccountPending(val) && (
          <Container className="card-red mb-0">
            <Row>
              <p className="strong">{accountTitle(index, ' Validada')}</p>
            </Row>
            <ValidatedFields prometeoInfo={prometeoInfo} />
          </Container>
        )}
      </Container>
    );
  };

  const accountInfo = (val: any, index: number) => (
    <div className="account-column" key={index}>
      <Row>
        <p className="strong">{accountTitle(index)}</p>
      </Row>
      <AccountFields val={val} />
    </div>
  );

  const accountValidationStatus = (val: any, index: number) => {
    const prometeoInfo = findPrometeoInfo(val);
    const isPending = isAccountPending(val);

    return (
      <div className="account-column" key={index}>
        {isPending ? (
          <>
            <Row>
              <p className="strong">{accountTitle(index, ' no Validada')}</p>
            </Row>
            <Row>
              <p className="account-pending-text">
                La cuenta no pudo validarse automáticamente. Se validará en las próximas horas.
              </p>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <p className="strong">{accountTitle(index, ' Validada')}</p>
            </Row>
            <ValidatedFields prometeoInfo={prometeoInfo} />
          </>
        )}
      </div>
    );
  };

  const buildBankContainer = () => {
    if (values?.bankAccounts && values?.bankAccounts?.length) {
      return values?.bankAccounts?.length === 2 ? (
        <Container className="cards-red">{values?.bankAccounts.map((b, i) => account(b, i))}</Container>
      ) : (
        account(values.bankAccounts[0], 0)
      );
    }
  };

  const hasAnyAccountPending =
    !prometeoError && !!values?.bankAccounts?.some((bankAccount) => isAccountPending(bankAccount));

  const isSingleAccountPending =
    hasAnyAccountPending && values?.bankAccounts?.length !== 2 && isAccountPending(values?.bankAccounts?.[0]);

  const isTwoAccountsWithPending = hasAnyAccountPending && values?.bankAccounts?.length === 2;

  const bankContainer = isTwoAccountsWithPending ? null : buildBankContainer();

  return (
    <Modal show={show} onHide={toggleModal} centered size="lg">
      <Card className="modal-card confirm-bank-card">
        {isSingleAccountPending ? (
          <CardHeader>
            <div>
              <img src={WarningRedIcon} alt="Warning red icon" />
              <p className="no-validation-title">Validación digital de cuenta no disponible</p>
              <p className="no-validation-text">
                La cuenta no pudo validarse automáticamente. Se validará en las próximas horas.
              </p>
            </div>
          </CardHeader>
        ) : prometeoError ? (
          <CardHeader>
            <div>
              <img src={WarningRedIcon} alt="Warning red icon" />
              <p className="no-validation-title">Validación digital de cuenta no disponible</p>
              <p className="no-validation-text">
                Para seguir con el proceso, necesitas hacer una validación manual
              </p>
            </div>
          </CardHeader>
        ) : (
          <CardHeader>
            <h1 className="mb-2">Confirmación de datos de cuenta bancaria</h1>
          </CardHeader>
        )}
        {isTwoAccountsWithPending && values?.bankAccounts && (
          <>
            <Container className="card-red">
              <div className="account-columns">{values.bankAccounts.map((b, i) => accountInfo(b, i))}</div>
            </Container>
            <Container className="card-red mb-0">
              <div className="account-columns">
                {values.bankAccounts.map((b, i) => accountValidationStatus(b, i))}
              </div>
            </Container>
          </>
        )}
        {bankContainer}
        <Card.Body>
          <Button className="primary-outlined-button w-100" onClick={() => handleNextStep(values)}>
            Los datos son correctos &gt;
          </Button>
          <Button className="primary-outlined-button w-100" onClick={toggleModal}>
            &lt; Editar datos
          </Button>
        </Card.Body>
      </Card>
    </Modal>
  );
};

export default ConfirmBankModal;
